import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslations } from 'use-intl';
import { EnterYourComment, Comment, Button } from 'components';
import { FirebaseContext } from 'context';
import {
  useLanguage,
  isMultiLingual,
  defaultLanguage,
  enabledLanguages
} from 'i18n';
import {
  rememberManyTranslated,
  translateOfferAnswer,
  setTranslateOfferAnswer
} from 'utils';
import queryString from 'query-string';

// How many comments a reader translates by hand before we offer to do the
// rest. Low enough to catch the intent early, high enough that one curious
// click does not trigger a prompt.
const MANUAL_TRANSLATIONS_BEFORE_PROMPT = 3;

function Comments({
  commentBeingEdited,
  comments,
  questionId,
  handleEditComment,
  handlePostComment,
  handleDeleteComment,
  idOfCommentBeingRepliedTo,
  postingOrEditingComment,
  setCommentBeingEdited,
  setIdOfCommentBeingRepliedTo,
  setQuestionIdQueryParam,
  users,
  sessionId,
  deletingComment,
  handleDialog,
  onlineUsers
}) {
  const { user, firebase } = useContext(FirebaseContext);
  const t = useTranslations('Review');
  const tCommon = useTranslations('Common');
  const { language } = useLanguage();
  const location = window?.location;
  const queryParams = queryString.parse(location?.search);
  const commentIdQueryParam = queryParams?.commentId;

  const [manualTranslations, setManualTranslations] = useState(0);
  // 'accepted' | 'declined' | null — how the reader last answered the offer.
  // Only 'accepted' changes future behaviour; a decline is remembered so the
  // answer is not lost, but it never silences the offer beyond this question.
  const [offerAnswer, setOfferAnswer] = useState(null);
  const [declinedHere, setDeclinedHere] = useState(false);
  // 'idle' | 'running' | 'error' — drives whether the toast holds open, and
  // whether comments still awaiting the batch are dimmed.
  const [batchState, setBatchState] = useState('idle');
  // Portalling the offer needs document, which does not exist during the
  // Gatsby build — so render it only once mounted on the client.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setOfferAnswer(translateOfferAnswer());
    setMounted(true);
  }, []);
  // Answering is per question: accepting translates the comments here, it does
  // not switch anything on globally. Reset when the reader moves on.
  useEffect(() => {
    setManualTranslations(0);
    setBatchState('idle');
    setDeclinedHere(false);
  }, [questionId]);

  // Comments in another language that have no cached translation yet — the
  // only ones an offer could do anything about. Keyed off the cache rather
  // than just the language, or the offer would reappear the moment a batch
  // finished, since `language` on a comment never changes. Legacy comments
  // carry no language, so they fall back to the instance default exactly as
  // the Comment component does.
  const pendingComments = comments.filter(
    (comment) =>
      (comment.language ?? defaultLanguage) !== language &&
      !comment.translationCache?.[language]
  );
  const showTranslateAll = isMultiLingual && pendingComments.length > 0;
  const languageName =
    enabledLanguages.find((l) => l.code === language)?.nativeName ?? language;

  /**
   * Translate the comments on THIS question that are missing the reader's
   * language, in one batched request rather than one per comment.
   *
   * Deliberately a one-shot action, not a setting. Every piece of state stays
   * per comment and reversible with that comment's own toggle, which is why
   * there is no global preference and no checkbox to explain — the reader
   * already has a control for each comment, and this is just a faster way of
   * pressing it several times.
   *
   * Awaited so the toast can hold while it runs: fire-and-forget meant the
   * toast vanished on click, the thread changed piecemeal seconds later, and
   * a failure looked exactly like success.
   */
  const acceptOffer = async () => {
    setOfferAnswer('accepted');
    setTranslateOfferAnswer('accepted');
    if (!firebase?.questions || !questionId) return;
    // Captured before the await: `comments` streams live, so by the time the
    // batch resolves this list has already shrunk to nothing.
    const pendingIds = pendingComments.map((c) => c.id);
    // Recorded BEFORE the await, for the same reason the single-comment path
    // does it: the subscription can deliver the batch's writes before this
    // promise resolves. Recording afterwards loses that race — the restore
    // effect runs on the incoming snapshot, finds no intent stored, shows the
    // original, and then nothing re-renders, because a localStorage write is
    // invisible to React and the effect's dependencies have not changed. The
    // comments then sit in the source language despite being translated.
    //
    // Marking them up front rather than reading ids back from the response is
    // safe: a comment the batch omitted simply has no cached translation, and
    // the restore path falls back to the original for those.
    rememberManyTranslated(pendingIds, language);
    setBatchState('running');
    try {
      await firebase.questions.translateCommentsBatch({
        sessionId,
        questionId,
        targetLanguage: language
      });
      setBatchState('idle');
    } catch (error) {
      console.error('[i18n] Batch comment translation failed:', error);
      setBatchState('error');
    }
  };

  const declineOffer = () => {
    setDeclinedHere(true);
    setOfferAnswer('declined');
    setTranslateOfferAnswer('declined');
  };

  const reachedThreshold =
    manualTranslations >= MANUAL_TRANSLATIONS_BEFORE_PROMPT;

  // A reader who has accepted before has already shown this is what they
  // want, so later questions offer immediately rather than making them
  // re-earn it with three more manual translations.
  //
  // Declining hides it for THIS question only. Persisting a decline as a
  // permanent silence — which is what an earlier version did — meant one
  // "no thanks" removed the feature for good, with no way back and nothing
  // to explain why.
  const offerEarned =
    !declinedHere && (offerAnswer === 'accepted' || reachedThreshold);

  // Holds open while the batch runs or has failed, so the work is visible.
  const showPrompt = showTranslateAll && (batchState !== 'idle' || offerEarned);
  return user ? (
    <Container>
      {/* Anchored to the viewport, not to the top of the thread. The offer
          fires wherever the reader happened to be when they translated their
          third comment — by then they have scrolled well past the top, so a
          message placed up there is never seen. Portalled to document.body
          because this app renders inside `motion` components, and any ancestor
          transform would silently make itself the containing block for
          position: fixed, anchoring the toast to a panel instead of the
          screen. */}
      {mounted &&
        showPrompt &&
        createPortal(
          <TranslateToast role="status" $error={batchState === 'error'}>
            <p>
              {batchState === 'running' && t('translatingAllComments')}
              {batchState === 'error' && t('translateError')}
              {batchState === 'idle' &&
                t('translateAllPrompt', { language: languageName })}
            </p>
            <PromptActions>
              {/* Decline first, accept last: the affirmative action sits
                  where the eye finishes, and the two are told apart by
                  weight (filled vs plain) rather than by colour alone.
                  Once the batch has failed the offer is spent, so only a way
                  to close it remains. */}
              <Button
                $variant="text"
                disabled={batchState === 'running'}
                onClick={declineOffer}>
                {batchState === 'idle'
                  ? t('translateAllDismiss')
                  : tCommon('close')}
              </Button>
              {batchState !== 'error' && (
                <Button
                  loading={batchState === 'running'}
                  onClick={acceptOffer}>
                  {t('translateAllAccept')}
                </Button>
              )}
            </PromptActions>
          </TranslateToast>,
          document.body
        )}
      {comments.length > 0
        ? comments
            .filter((comment) => !comment.parentCommentId)
            .map((comment, i, arr) => (
              <Comment
                i={i}
                arr={arr}
                parentCommentId={null}
                commentBeingEdited={commentBeingEdited}
                comments={comments}
                comment={comment}
                handleEditComment={handleEditComment}
                handlePostComment={handlePostComment}
                handleDeleteComment={handleDeleteComment}
                idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
                key={comment.id}
                level={0}
                postingOrEditingComment={postingOrEditingComment}
                setCommentBeingEdited={setCommentBeingEdited}
                setIdOfCommentBeingRepliedTo={setIdOfCommentBeingRepliedTo}
                user={user}
                setQuestionIdQueryParam={setQuestionIdQueryParam}
                users={users}
                commentIdQueryParam={commentIdQueryParam}
                sessionId={sessionId}
                deletingComment={deletingComment}
                handleDialog={handleDialog}
                onlineUsers={onlineUsers}
                batchTranslating={batchState === 'running'}
                onManualTranslate={() =>
                  setManualTranslations((count) => count + 1)
                }
              />
            ))
        : null}
      <EnterYourComment
        handlePostComment={handlePostComment}
        postingOrEditingComment={postingOrEditingComment}
        users={users}
        uid={user.uid}
        sessionId={sessionId}
      />
    </Container>
  ) : null;
}

/**
 * Viewport-anchored offer.
 *
 * role="status" rather than alert: this is an offer, not a problem, so screen
 * readers announce it without interrupting, and focus is deliberately NOT
 * moved — stealing it mid-read to ask an optional question would be worse
 * than the scrolling problem this solves.
 *
 * Bottom rather than top: top reads as an alert, and the header already lives
 * there. z-index 3 puts it above the thread but below the Dialog overlay's 4,
 * so an open modal's scrim correctly covers it. It does not auto-dismiss —
 * it asks a question, and a question that disappears on its own looks
 * answered when it was not.
 */
const TranslateToast = styled.div`
  background-color: #fff;
  border: 1px solid rgba(0, 123, 255, 0.25);
  border-radius: var(--border-radius-md);
  bottom: 1rem;
  box-shadow: 0 0.25rem 1rem rgb(0 0 0 / 0.15);
  /* Centred by pinning both edges and letting auto margins do the work,
     rather than left: 50% + translateX(-50%). The slide-up animation below
     owns the transform property, and a centring transform there would be
     overwritten by the keyframes' final translateY — dropping the toast half
     its width to the left just as it settled.

     One rule covers both sizes: min() caps it at 24rem on wide screens and
     falls back to the viewport minus a 1rem gutter on narrow ones. */
  left: 0;
  margin: 0 auto;
  max-width: min(24rem, calc(100% - 2rem));
  padding: 0.875rem 1rem;
  position: fixed;
  right: 0;
  z-index: 3;

  /* --dark-blue, not --blue: #007bff is ~3.6:1 on white, under the 4.5:1
     minimum for body text, and it left the question quieter than the two
     answers to it. The message leads now; the actions follow. */
  p {
    color: ${({ $error }) => ($error ? 'var(--red)' : 'var(--dark-blue)')};
    font-size: 0.875rem;
    line-height: 1.45;
    margin: 0 0 0.75rem;
    text-wrap: pretty;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: toast-in 200ms ease-out;
  }

  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translateY(0.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Sits to one side on wider screens so it covers as little of the thread as
     possible, and never spans a full desktop width for two short lines. */
`;

/**
 * Right-aligned and scaled down. The shared Button is sized for forms and
 * inline comment actions, so at its default size two of them dwarfed the
 * question and left the toast mostly empty space.
 */
const PromptActions = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  button {
    font-size: 0.8125rem;
    min-height: 0;
    padding: 0.5rem 0.875rem;
  }
`;

const Container = styled.div`
  border-radius: var(--border-radius-md);
  margin: 0 1.25rem;

  @media (min-width: 1200px) {
    margin: 0;
  }
`;

export default Comments;
